import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Box,
  alpha,
  useTheme,
  Fade,
  Skeleton,
  InputBase,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  TrendingUp,
  Speed,
  Timeline,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useAppContext } from '../contexts/ContextProvider';

const ModernHome: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, municipioAtivo } = useAuth();
  const { menus, loading } = useAppContext();

  const allSubmenus = menus.flatMap((menu) =>
    (menu.submenus || []).map((submenu: any) => ({
      ...submenu,
      menu_nome: menu.nome,
      menu_icone: menu.icone,
    }))
  );

  const getRandomIcon = (index: number) => {
    const icons = [DashboardIcon, TrendingUp, Speed, Timeline];
    return icons[index % icons.length];
  };

  const getRandomColor = (index: number) => {
    const colors = ['#007AF0', '#02786D', '#217B00', '#E76F00'];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mb: 6 }}>
          <Skeleton variant="text" width="60%" height={60} />
          <Skeleton variant="text" width="40%" height={30} sx={{ mt: 1 }} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 4 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Barra de Pesquisa - compacta e limpa */}
      <Fade in timeout={1000}>
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
            transition: 'border-color 0.2s, box-shadow 0.2s',
            '&:focus-within': {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.12)}`,
            },
          }}
        >
          <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 20, flexShrink: 0 }} />
          <InputBase
            placeholder="Buscar dashboards..."
            fullWidth
            sx={{
              fontSize: '0.875rem',
              py: 0.25,
              '& .MuiInputBase-input': {
                '&::placeholder': {
                  color: theme.palette.text.secondary,
                  opacity: 0.8,
                },
              },
            }}
          />
        </Box>
      </Fade>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Fade in timeout={1000}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #007AF0 0%, #0051A2 100%)',
                color: 'white',
                height: '100%',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {allSubmenus.length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                      Dashboards Disponíveis
                    </Typography>
                  </Box>
                  <DashboardIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Fade in timeout={1200}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #515162 0%, #3A3A47 100%)',
                color: 'white',
                height: '100%',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {menus.length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                      Categorias
                    </Typography>
                  </Box>
                  <Timeline sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Fade in timeout={1400}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #02786D 0%, #50BBAF 100%)',
                color: 'white',
                height: '100%',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      98%
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                      Dados Atualizados
                    </Typography>
                  </Box>
                  <TrendingUp sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Fade in timeout={1600}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #217B00 0%, #54C241 100%)',
                color: 'white',
                height: '100%',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      Fast
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                      Performance
                    </Typography>
                  </Box>
                  <Speed sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Grid>

      {/* Dashboards Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight={700}>
          Seus Dashboards
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Acesse rapidamente seus indicadores mais importantes
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {allSubmenus.map((submenu, index) => {
          const Icon = getRandomIcon(index);
          const color = getRandomColor(index);

          return (
            <Grid item xs={12} sm={6} md={4} key={submenu.id}>
              <Fade in timeout={800 + index * 100}>
                <Card
                  sx={{
                    height: '100%',
                    position: 'relative',
                    overflow: 'visible',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: color,
                      borderRadius: '8px 8px 0 0',
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => navigate(`/dashboard/${submenu.id}`)}
                    sx={{ height: '100%' }}
                  >
                    <CardContent sx={{ pt: 3 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(color, 0.1),
                            color: color,
                            mr: 2,
                          }}
                        >
                          <Icon />
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                          >
                            {submenu.menu_nome}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {submenu.nome}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Clique para visualizar os indicadores e análises detalhadas
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Fade>
            </Grid>
          );
        })}
      </Grid>

      {allSubmenus.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: 4,
          }}
        >
          <DashboardIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nenhum dashboard disponível no momento
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Entre em contato com o administrador para configurar seus dashboards
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default ModernHome;
